import { useEffect, useRef, useState } from "react";
import sanitizeHtml from "sanitize-html";

import { FixedClickableTextPanel } from "@/Common/Components/ClickableText/FixedClickableTextPanel";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { TextAreaResizable } from "@/Common/Components/Inputs/TextAreaResizable";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { FaxCoverSheetClickableTextContext } from "@/ExportableFile/Models/enum/FaxCoverSheetClickableTextContext";

import styles from "./FaxCoverSheetEditorSection.module.scss";

interface IFaxCoverSheetEditorSectionProps {
    containsCoverSheet?: boolean;
    isSubjectValid?: boolean;
    isMessageValid?: boolean;
    containerClassName?: string;
    subject?: string;
    message?: string;
    validate: (validSubject: boolean, validMessage: boolean) => void;
    onSubjectChange?: (text: string) => void;
    onMessageChange?: (text: string) => void;
    onCheckboxChange?: (value: boolean) => void;
}
const MAX_SUBJECT_LENGTH: number = 68;
const MAX_MESSAGE_LENGTH: number = 720;

function FaxCoverSheetEditorSection(props: Readonly<IFaxCoverSheetEditorSectionProps>): JSX.Element {
    const { getText } = useTranslation();
    const messageWrapperRef = useRef<HTMLDivElement>();
    const subjectWrapperRef = useRef<HTMLDivElement>();
    const [checked, setChecked] = useState(false);
    const [activeClickableText, setActiveClickableText] = useState<FaxCoverSheetClickableTextContext | null>(null);

    function onCheckboxChange(isChecked: boolean): void {
        if (!isChecked) {
            props.onSubjectChange?.("");
            props.onMessageChange?.("");
            props.validate?.(true, true);
        }

        setChecked(isChecked);
        props.onCheckboxChange?.(isChecked);
    }

    useEffect(() => {
        if (cacheStore.ClinicSettings.EnableFaxCoverSheet) {
            props.validate?.(isTextboxValid(props.subject), isTextboxValid(props.message));
        }
    }, [props.subject, props.message]);

    function isTextboxValid(text: string): boolean {
        if (!text) {
            return true;
        }

        return sanitizeHtml(text, { allowedTags: [] }) === text;
    }

    return (
        <div className={props.containerClassName}>
            <CheckboxControl
                checked={checked}
                label={props.containsCoverSheet ? getText("FaxCoverSheet_ReplaceCoverSheetLabel") : getText("FaxCoverSheet_AddCoverSheetLabel")}
                onChange={isChecked => onCheckboxChange(isChecked)}
                className={styles.checkbox}
                dataCy="fax-cover-sheet-checkbox"
            />
            {props.containsCoverSheet && !checked && <div className={styles.note}>{getText("FaxCoverSheet_ResentNoticeLabel")}</div>}
            {checked && (
                <div>
                    <div className={styles.note}>{getText("FaxCoverSheet_NoteLabel")}</div>

                    <label>{getText("SubjectLabel")}</label>
                    <div ref={subjectWrapperRef}>
                        <TextboxControl
                            id={`coversheet-subject-${FaxCoverSheetClickableTextContext.Subject}`}
                            value={props.subject}
                            onChange={text => props.onSubjectChange?.(text)}
                            maxLength={MAX_SUBJECT_LENGTH}
                            valid={props.isSubjectValid}
                        />
                    </div>
                    <div className={styles.subjectClickableTextContainer}>
                        {!props.isSubjectValid &&
                            <label className="label-error">{getText("FaxCoverSheet_ErrorMessage")}</label>
                        }
                        <div>
                            <FixedClickableTextPanel
                                cssClass="text-helper-container"
                                context="FaxCoverSheetSubject"
                                inputElementId={`coversheet-subject-${FaxCoverSheetClickableTextContext.Subject}`}
                                isLinkDisabled={false}
                                isClickableTextPanelOpen={activeClickableText === FaxCoverSheetClickableTextContext.Subject}
                                onClickableTextPanelOpen={() => setActiveClickableText(FaxCoverSheetClickableTextContext.Subject)}
                                onClickableTextPanelClose={() => setActiveClickableText(null)}
                                linkClassName={styles.clickableTextLink}
                                textboxHeight={subjectWrapperRef.current?.clientHeight ?? 0}
                                clickableTextPanelCssStyle={{ bottom: "0px" }}
                                reduceHeight
                                isAboveLabel
                                closeOnOutsideClick
                            />
                        </div>
                    </div>
                    <label>{getText("MessageLabel")}</label>
                    <div>
                        <div ref={messageWrapperRef}>
                            <TextAreaResizable
                                maxLength={MAX_MESSAGE_LENGTH}
                                invalid={!props.isMessageValid}
                                showCounter
                                id={`coversheet-message-${FaxCoverSheetClickableTextContext.Message}`}
                                minHeightPx={50}
                                maxHeightPx={200}
                                containerClassName={styles.messageContainer}
                                counterClassName={styles.maxCharactersCounter}
                                verticalResizeOnly
                                text={props.message}
                                onChange={text => props.onMessageChange?.(text)}
                            />
                        </div>
                        <div>
                            <FixedClickableTextPanel
                                cssClass="text-helper-container"
                                context="FaxCoverSheetMessage"
                                inputElementId={`coversheet-message-${FaxCoverSheetClickableTextContext.Message}`}
                                isLinkDisabled={false}
                                isClickableTextPanelOpen={activeClickableText === FaxCoverSheetClickableTextContext.Message}
                                onClickableTextPanelOpen={() => setActiveClickableText(FaxCoverSheetClickableTextContext.Message)}
                                onClickableTextPanelClose={() => setActiveClickableText(null)}
                                linkClassName={styles.clickableTextLink}
                                textboxHeight={messageWrapperRef.current?.clientHeight ?? 0}
                                clickableTextPanelCssStyle={{ bottom: "0px" }}
                                isAboveLabel
                                reduceHeight
                                closeOnOutsideClick
                            />
                        </div>
                        {!props.isMessageValid &&
                            <label className="label-error">{getText("FaxCoverSheet_ErrorMessage")}</label>
                        }
                    </div>
                </div>
            )}
        </div>
    );
}

export { FaxCoverSheetEditorSection };
